import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// Roles and permissions on the Team page (pmfit K6-7, Phelipe 2026-10-06).
// One pop-up, three uses:
//   person  — one teammate's permissions on top of their role ("· Custom"),
//             with "Reset to company settings"
//   role    — what everyone in a role can do, with "Reset to ConsLog defaults"
//   project — one person's tools on one project (project Team list), with
//             "Reset to their company settings"
// The server sends the whole picture (Access::PersonEditor / RoleEditor
// #payload) and decides what may change; this only draws it and sends it
// back. Saving updates the row's badge in place — no page reload.
export default class extends Controller {
  static targets = [
    "modal", "title", "subtitle", "body", "error", "resetButton", "saveButton", "previewButton",
    "rolesModal", "roleRow",
    "confirmModal", "confirmTitle", "confirmMessage", "confirmButton"
  ]

  static values = { personUrl: String, roleUrl: String, projectUrl: String, previewUrl: String, i18n: Object }

  // ── Opening ───────────────────────────────────────────────────────────

  openRoles() {
    this.rolesModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeRoles() {
    this.rolesModalTarget.classList.add("hidden")
    if (this.modalTarget.classList.contains("hidden")) document.body.style.overflow = ""
  }

  async openPerson(event) {
    const id = event.currentTarget.dataset.userId
    this._kind = "person"
    this._userId = id
    this._url = this.personUrlValue.replace(":id", encodeURIComponent(id))
    await this._load()
  }

  async openProjectMember(event) {
    const id = event.currentTarget.dataset.userId
    this._kind = "project"
    this._url = this.projectUrlValue.replace(":id", encodeURIComponent(id))
    await this._load()
  }

  async openRole(event) {
    const role = event.currentTarget.dataset.role
    this._kind = "role"
    this._url = this.roleUrlValue.replace(":role", encodeURIComponent(role))
    this.closeRoles()
    await this._load()
  }

  async _load() {
    try {
      const response = await fetch(this._url, { headers: { Accept: "application/json" } })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showToast(data.error || this._t("load_failed"), "error")
        return
      }
      this._data = data
      this._render()
      this.modalTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    } catch {
      showToast(this._t("load_failed"), "error")
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.confirmModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Drawing ───────────────────────────────────────────────────────────

  _render() {
    this._previewing = false
    const d = this._data
    const person = this._kind === "person"
    const onProject = this._kind === "project"
    const toolsOnly = onProject || (person && d.mode === "tools_only")
    const customTag = d.custom ? ` <span class="text-gray-400">·</span> <span class="font-medium text-brand">${this._t("custom")}</span>` : ""

    this.titleTarget.textContent = person || onProject ? d.user.name : `${this._t("role_title")}: ${d.role_name}`
    if (onProject) {
      this.subtitleTarget.innerHTML = `${this._esc(this._t("on_project").replace("%{project}", d.project.name))} <span class="text-gray-400">·</span> ${this._esc(d.role_name)}${customTag}`
    } else if (person) {
      this.subtitleTarget.innerHTML = `${this._esc(d.role_name)}${customTag} <span class="text-gray-400">—</span> ${this._esc(d.role_description)}`
    } else {
      this.subtitleTarget.innerHTML = `${this._esc(d.role_description)} <span class="text-gray-400">·</span> ${this._t("applies_to").replace("%{count}", d.people)}`
    }
    this.errorTarget.classList.add("hidden")

    const parts = []
    if (person && d.view_only) parts.push(this._note(this._t("view_only_note"), "amber"))
    if (onProject) parts.push(this._note(this._t("project_note"), "gray"))
    else if (toolsOnly) parts.push(this._note(this._t("tools_only_note"), "gray"))
    if (!toolsOnly) {
      parts.push(this._scopeSection(d))
      parts.push(this._createSection(d))
      parts.push(this._flagsSection(d))
    }
    parts.push(this._toolsSection(d))
    parts.push(this._historySection(d))
    this.bodyTarget.innerHTML = parts.join("")

    const canReset = d.custom && (!person || d.mode === "full")
    this.resetButtonTarget.classList.toggle("hidden", !canReset)
    const canPreview = person && this.hasPreviewButtonTarget && this.previewUrlValue
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.classList.toggle("hidden", !canPreview)
      this.previewButtonTarget.textContent = this._t("preview")
    }
    this.saveButtonTarget.classList.remove("hidden")
    this.resetButtonTarget.textContent = onProject ? this._t("reset_project") : person ? this._t("reset_person") : this._t("reset_role")
  }

  _section(title, hint, inner) {
    return `<section>
      <h4 class="text-[13px] font-semibold text-gray-800">${this._esc(title)}</h4>
      ${hint ? `<p class="text-[13px] text-gray-500 mt-0.5">${this._esc(hint)}</p>` : ""}
      <div class="mt-2">${inner}</div>
    </section>`
  }

  _note(text, tone) {
    const tones = { amber: "border-amber-200 bg-amber-50 text-amber-800", gray: "border-gray-200 bg-gray-50 text-gray-700" }
    return `<div class="rounded-lg border ${tones[tone] || tones.gray} px-3 py-2 text-[13px]">${this._esc(text)}</div>`
  }

  _changedDot(changed) {
    return changed
      ? `<span class="inline-block w-1.5 h-1.5 rounded-full bg-brand ml-1 align-middle" title="${this._esc(this._t("changed_from_role"))}"></span>`
      : ""
  }

  _scopeSection(d) {
    const s = d.scope
    if (s.fixed) {
      const label = (s.options[0] || {}).label || ""
      return this._section(this._t("projects_they_see"), this._t("scope_fixed_hint"),
        `<p class="text-[13px] text-gray-700">${this._esc(label)}</p>`)
    }
    const radios = s.options.map(o => `
      <label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer mr-5">
        <input type="radio" name="access_scope" value="${this._esc(o.value)}" ${o.value === s.value ? "checked" : ""}
               class="text-brand focus:ring-brand">
        ${this._esc(o.label)}
      </label>`).join("")
    return this._section(this._t("projects_they_see"), null, radios)
  }

  _createSection(d) {
    const c = d.create
    const boxes = c.options.map(o => {
      const on = c.value.includes(o.value)
      const changed = on !== c.default.includes(o.value)
      return `<label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer mr-5">
        <input type="checkbox" name="access_create" value="${this._esc(o.value)}" ${on ? "checked" : ""}
               class="rounded border-gray-300 text-brand focus:ring-brand">
        ${this._esc(o.label)}${this._changedDot(changed)}
      </label>`
    }).join("")
    return this._section(this._t("create_projects_in"), this._t("create_hint"), boxes)
  }

  _flagsSection(d) {
    const f = d.flags
    const boxes = f.options.map(o => {
      const on = !!f.value[o.value]
      const locked = (f.locked || []).includes(o.value)
      const changed = on !== !!f.default[o.value]
      return `<label class="inline-flex items-center gap-2 text-[13px] ${locked ? "text-gray-400" : "text-gray-700 cursor-pointer"} mr-5"
                     ${locked ? `title="${this._esc(this._t("locked_hint"))}"` : ""}>
        <input type="checkbox" name="access_flag" value="${this._esc(o.value)}" ${on ? "checked" : ""} ${locked ? "disabled" : ""}
               class="rounded border-gray-300 text-brand focus:ring-brand">
        ${this._esc(o.label)}${this._changedDot(changed)}
      </label>`
    }).join("")
    return this._section(this._t("company"), null, boxes)
  }

  _toolsSection(d) {
    const t = d.tools
    const rank = Object.fromEntries(t.levels.map((l, i) => [l.value, i]))
    const head = `<tr class="text-gray-500">
        <th class="text-left font-medium py-1.5 pr-2">${this._t("tool")}</th>
        ${t.levels.map(l => `<th class="font-medium py-1.5 px-1 text-center">${this._esc(l.label)}</th>`).join("")}
      </tr>`
    const rows = t.options.map(o => {
      const cur = t.value[o.value]
      const max = (t.max || {})[o.value] || "admin"
      const changed = cur !== t.default[o.value]
      const cells = t.levels.map(l => {
        const unavailable = l.value === "admin" && !o.admin
        const aboveMax = rank[l.value] > rank[max] && l.value !== cur
        const disabled = unavailable || aboveMax
        return `<td class="py-1.5 px-1 text-center">
          ${unavailable ? `<span class="text-gray-300">—</span>` : `<input type="radio" name="access_tool_${this._esc(o.value)}" value="${l.value}"
                 ${cur === l.value ? "checked" : ""} ${disabled ? "disabled" : ""}
                 aria-label="${this._esc(o.label)}: ${this._esc(l.label)}"
                 class="text-brand focus:ring-brand ${disabled ? "opacity-40" : "cursor-pointer"}">`}
        </td>`
      }).join("")
      return `<tr class="border-t border-gray-100"><td class="py-1.5 pr-2 text-gray-800">${this._esc(o.label)}${this._changedDot(changed)}</td>${cells}</tr>`
    }).join("")
    const table = `<table class="w-full text-[13px]"><thead>${head}</thead><tbody>${rows}</tbody></table>`
    return this._section(this._t("inside_a_project"), this._t("levels_hint"), table)
  }

  _historySection(d) {
    const items = d.history || []
    if (!items.length) return this._section(this._t("history"), null, `<p class="text-[13px] text-gray-400">${this._t("no_history")}</p>`)
    const list = items.map(h => `<li class="py-1.5 border-t border-gray-100 first:border-t-0">
        <div class="text-[13px] text-gray-500">${this._esc(h.at || "")} · ${this._esc(h.actor || "")}</div>
        <div class="text-[13px] text-gray-800">${(h.lines || []).map(l => this._esc(l)).join("<br>")}</div>
      </li>`).join("")
    return `<details class="group"><summary class="text-[13px] font-semibold text-gray-800 cursor-pointer select-none">
        ${this._t("history")} <span class="font-normal text-gray-500">(${items.length})</span></summary>
      <ul class="mt-2">${list}</ul></details>`
  }

  // ── Preview as this person (pmfit K6-7 "D") ──────────────────────────
  // The side menu they'd get on one of their projects with what's in the
  // pop-up right now — saved or not. The server works it out by the real
  // rules; nothing is saved.

  togglePreview() {
    if (this._previewing) {
      this._render()
      return
    }
    this._stashForm()
    this._previewing = true
    this.previewButtonTarget.textContent = this._t("back_to_editing")
    this.resetButtonTarget.classList.add("hidden")
    this.saveButtonTarget.classList.add("hidden")
    const projects = this._data.preview_projects || []
    if (!projects.length) {
      this.bodyTarget.innerHTML = `<p class="text-[13px] text-gray-500">${this._t("no_projects")}</p>`
      return
    }
    const options = projects.map(p => `<option value="${p.id}">${this._esc(p.name)}</option>`).join("")
    this.bodyTarget.innerHTML = `
      ${this._note(this._t("preview_note"), "gray")}
      <label class="block text-[13px] font-medium text-gray-700 mt-4 mb-1">${this._t("preview_pick_project")}</label>
      <select data-preview-project class="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:outline-none focus:border-brand">${options}</select>
      <div data-preview-result class="mt-4"></div>`
    const select = this.bodyTarget.querySelector("[data-preview-project]")
    select.addEventListener("change", () => this._loadPreview(select.value))
    this._loadPreview(select.value)
  }

  async _loadPreview(projectId) {
    const box = this.bodyTarget.querySelector("[data-preview-result]")
    if (!box) return
    box.innerHTML = `<p class="text-[13px] text-gray-400">…</p>`
    try {
      const url = this.previewUrlValue.replace(":id", encodeURIComponent(this._userId))
      const response = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ ...this._stash, project_id: projectId })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        box.innerHTML = `<p class="text-[13px] text-red-600">${this._esc(data.error || this._t("load_failed"))}</p>`
        return
      }
      const chip = (level, label) => {
        const tones = {
          none: "bg-gray-100 text-gray-500",
          read_only: "bg-amber-100 text-amber-800",
          standard: "bg-green-100 text-green-800",
          admin: "bg-brand/10 text-brand"
        }
        return `<span class="px-2 py-0.5 rounded-full text-[13px] font-medium ${tones[level] || tones.standard}">${this._esc(level === "none" ? this._t("hidden") : label)}</span>`
      }
      const rows = data.menu.map(m => `
        <li class="flex items-center justify-between gap-3 py-1.5 border-t border-gray-100 first:border-t-0">
          <span class="text-[13px] ${m.level === "none" ? "text-gray-400 line-through" : "text-gray-800"}">${this._esc(m.label)}</span>
          ${chip(m.level, m.level_label)}
        </li>`).join("")
      box.innerHTML = `
        <div class="rounded-lg border border-gray-200 px-3 py-1"><ul>${rows}</ul></div>
        <p class="text-[13px] text-gray-600 mt-3"><span class="font-medium">${this._t("create_projects_in")}:</span> ${this._esc(data.create.length ? data.create.join(", ") : this._t("create_none"))}</p>
        <p class="text-[13px] text-gray-600 mt-1"><span class="font-medium">${this._t("company")}:</span> ${this._esc(data.flags.length ? data.flags.join(", ") : "—")}</p>`
    } catch {
      box.innerHTML = `<p class="text-[13px] text-red-600">${this._t("load_failed")}</p>`
    }
  }

  // Keep what's ticked in the pop-up so Preview and "Back to editing" show it.
  _stashForm() {
    const body = this._collect()
    this._stash = body
    const d = this._data
    if (body.tools) Object.assign(d.tools.value, body.tools)
    if (body.create) d.create.value = body.create
    if (body.flags) Object.assign(d.flags.value, body.flags)
    if (body.scope) d.scope.value = body.scope
  }

  // ── Saving ────────────────────────────────────────────────────────────

  _collect() {
    const d = this._data
    const body = { tools: {} }
    d.tools.options.forEach(o => {
      const picked = this.bodyTarget.querySelector(`input[name="access_tool_${CSS.escape(o.value)}"]:checked`)
      if (picked) body.tools[o.value] = picked.value
    })
    const toolsOnly = this._kind === "project" || (this._kind === "person" && d.mode === "tools_only")
    if (!toolsOnly) {
      body.create = [...this.bodyTarget.querySelectorAll('input[name="access_create"]:checked')].map(i => i.value)
      if (!body.create.length) body.create_none = "1"
      body.flags = {}
      d.flags.options.forEach(o => {
        const box = this.bodyTarget.querySelector(`input[name="access_flag"][value="${CSS.escape(o.value)}"]`)
        if (box) body.flags[o.value] = box.checked
      })
      const scope = this.bodyTarget.querySelector('input[name="access_scope"]:checked')
      if (scope) body.scope = scope.value
    }
    return body
  }

  async save() {
    if (this._previewing) this._render()
    const btn = this.saveButtonTarget
    btn.disabled = true
    try {
      const response = await fetch(this._url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(this._collect())
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showError(data.error || this._t("save_failed"))
        return
      }
      this._afterChange(data)
      this.close()
      showToast(data.message || this._t("saved"))
    } catch {
      this._showError(this._t("save_failed"))
    } finally {
      btn.disabled = false
    }
  }

  askReset() {
    const kind = this._kind
    this.confirmTitleTarget.textContent =
      kind === "project" ? this._t("reset_project") : kind === "person" ? this._t("reset_person") : this._t("reset_role")
    if (kind === "project") {
      this.confirmMessageTarget.textContent = this._t("reset_project_confirm").replace("%{name}", this._data.user.name)
    } else if (kind === "person") {
      this.confirmMessageTarget.textContent = this._t("reset_person_confirm").replace("%{name}", this._data.user.name).replace("%{role}", this._data.role_name)
    } else {
      this.confirmMessageTarget.textContent = this._t("reset_role_confirm").replace("%{role}", this._data.role_name)
    }
    this.confirmModalTarget.classList.remove("hidden")
  }

  cancelReset() {
    this.confirmModalTarget.classList.add("hidden")
  }

  async confirmReset() {
    const btn = this.confirmButtonTarget
    btn.disabled = true
    try {
      const response = await fetch(this._url, { method: "DELETE", headers: this._headers() })
      const data = await response.json().catch(() => ({}))
      this.confirmModalTarget.classList.add("hidden")
      if (!response.ok) {
        this._showError(data.error || this._t("save_failed"))
        return
      }
      this._afterChange(data)
      this.close()
      showToast(data.message || this._t("saved"))
    } catch {
      this._showError(this._t("save_failed"))
    } finally {
      btn.disabled = false
    }
  }

  // The row's badge (person) or the roles list's tag (role), in place.
  _afterChange(data) {
    if (this._kind === "project") {
      const tag = document.querySelector(`[data-project-access-tag="${data.user.id}"]`)
      if (tag) {
        tag.classList.toggle("hidden", !data.custom)
        tag.title = (data.custom_lines || []).join("\n")
      }
      return
    }
    if (this._kind === "person") {
      const badge = document.querySelector(`[data-role-badge="${data.user.id}"]`)
      if (!badge) return
      const tag = badge.querySelector("[data-custom-tag]")
      if (data.custom) {
        badge.title = (data.custom_lines || []).join("\n")
        if (!tag) {
          const span = document.createElement("span")
          span.className = "opacity-70"
          span.dataset.customTag = ""
          span.textContent = `· ${this._t("custom")}`
          const lock = badge.querySelector("svg")
          lock ? badge.insertBefore(span, lock) : badge.appendChild(span)
        }
      } else {
        badge.removeAttribute("title")
        tag?.remove()
      }
    } else {
      const row = this.roleRowTargets.find(r => r.dataset.role === data.role)
      row?.querySelector("[data-custom-tag]")?.classList.toggle("hidden", !data.custom)
    }
  }

  _showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _t(key) {
    return (this.i18nValue || {})[key] || key
  }

  _esc(value) {
    return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
